import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, makeStateKey, PLATFORM_ID, REQUEST, signal, TransferState, } from '@angular/core';
import { catchError, EMPTY, map, Observable, of, tap, timeout } from 'rxjs';
import { ENVIRONMENT } from '@env/environment.token';
import { IpBlacklistApiService } from '@services/api/ip-blacklist-api.service';

export interface BlacklistItem {
  type: 'ip' | 'ipv4' | 'cidr';
  value: string;
}

export interface BlacklistConfig {
  items: BlacklistItem[];
  updatedAt?: string;
}

export const ADS_ALLOWED = makeStateKey<boolean>('adsAllowed');

let configCache: { data: BlacklistConfig; expiresAt: number } | null = null;

@Injectable({ providedIn: 'root' })
export class IpBlacklistService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly transferState = inject(TransferState);
  private readonly request = inject(REQUEST, { optional: true });
  private readonly env = inject(ENVIRONMENT);
  private readonly IpBlacklistService = inject(IpBlacklistApiService);

  readonly adsAllowed = signal(this.transferState.get(ADS_ALLOWED, true));

  resolve(): Observable<unknown> {
    if (isPlatformBrowser(this.platformId) || !this.env.ipBlacklist) {
      return EMPTY;
    }

    const ip = this.clientIp();
    if (!ip) {
      return EMPTY;
    }

    return this.loadConfig().pipe(
      map((config) => !config || this.matchIp(ip, config.items) === null),
      tap((allowed) => {
        this.adsAllowed.set(allowed);
        this.transferState.set(ADS_ALLOWED, allowed);
      }),
    );
  }

  private clientIp(): string | null {
    const headers = this.request?.headers;
    if (!headers) {
      return null;
    }

    const forwarded = headers.get('x-forwarded-for');
    return forwarded ? forwarded.split(',')[0].trim() : (headers.get('x-real-ip')?.trim() ?? null);
  }

  private loadConfig(): Observable<BlacklistConfig | null> {
    if (configCache && configCache.expiresAt > Date.now()) {
      return of(configCache.data);
    }


    return this.IpBlacklistService.getConfigs().pipe(
      timeout(this.env.ipBlacklist!.timeoutMs),
      map((config) => (Array.isArray(config?.items) ? config : null)),
      tap((config) => {
        if (config) {
          configCache = { data: config, expiresAt: Date.now() + this.env.ipBlacklist!.ttlMs };
        }
      }),
      catchError(() => of(configCache?.data ?? null)),
    );
  }

  private matchIp(ip: string, items: BlacklistItem[]): BlacklistItem | null {
    const ipLong = this.ipToLong(ip);
    if (ipLong === null) {
      return null;
    }

    return (
      items.find((item) => {
        const value = item?.value?.trim();
        if (!value) {
          return false;
        }

        if (item.type === 'ip' || item.type === 'ipv4') {
          return ip === value || ipLong === this.ipToLong(value);
        }

        return item.type === 'cidr' && this.ipInCidr(ipLong, value);
      }) ?? null
    );
  }

  private ipToLong(ip: string): number | null {
    const octets = ip.split('.').map(Number);

    if (octets.length !== 4 || octets.some((o) => !Number.isInteger(o) || o < 0 || o > 255)) {
      return null;
    }

    return octets.reduce((acc, octet) => acc * 256 + octet, 0);
  }

  private ipInCidr(ipLong: number, cidr: string): boolean {
    const [subnet, bits] = cidr.split('/');
    const maskBits = Number(bits);

    if (!Number.isInteger(maskBits) || maskBits < 0 || maskBits > 32) {
      return false;
    }

    const subnetLong = this.ipToLong(subnet.trim());
    if (subnetLong === null) {
      return false;
    }

    if (maskBits === 0) {
      return true;
    }

    const mask = (0xffffffff << (32 - maskBits)) >>> 0;
    return ((ipLong & mask) >>> 0) === ((subnetLong & mask) >>> 0);
  }
}
